import { createContext } from 'react';
import type { tAlertCenterContext } from './AlertCenter.types';

/**
 * Default value for consumers rendered outside the provider (tests, storybook,
 * pre-auth routes). Everything is empty and every seam is a no-op: an alert
 * surface must degrade to "nothing to show", never to a crash.
 *
 * Exported (RET-1871) so `AlertCenterBoundary`'s Tier-1 fallback can re-provide
 * this exact safe shape when `AlertCenterProvider` itself throws during
 * snapshot assembly.
 */
export const DEFAULT_ALERT_CENTER_CONTEXT: tAlertCenterContext = {
  alerts: [],
  derivedAlerts: [],
  wideAlert: null,
  waitingCriticalAlerts: [],
  wideAlertShowsCta: false,
  badgeCount: 0,
  // Fail-safe OFF: outside the provider (or when it crashed) no Alert Center
  // surface may mount.
  areSurfacesEnabled: false,
  acknowledgeAlert: () => {},
  acknowledgedKeys: [],
  acknowledgedAt: {},
  alertSessionLog: [],
  loadingAlertIds: [],
  registerInlineAlert: () => () => {},
  inlineAlertIds: [],
  inlineAlertSources: {},
  sellerPauseReason: null,
  quotaExceededCount: null,
  metaPricingNotice: null,
  openSupport: () => {},
  reportBoardExpiredLead: () => {},
  requestStageThresholdEdit: () => {},
  stageThresholdEditRequest: null,
  consumeStageThresholdEditRequest: () => {},
  setPastDueZoneOnScreen: () => {},
};

export const AlertCenterContext = createContext<tAlertCenterContext>(
  DEFAULT_ALERT_CENTER_CONTEXT,
);
